import type { BillingTier } from './billing';

/**
 * Structured 403s the API sends when a plan or an unverified email blocks an action:
 *   { error, code: 'PLAN_FEATURE_REQUIRED', feature, requiredTier }
 *   { error, code: 'PLAN_LIMIT_REACHED', limit, current, max, requiredTier }
 *   { error, code: 'EMAIL_VERIFICATION_REQUIRED', feature }
 * Older APIs send a plain `{ error }` instead; those are left to the page's own error handling.
 */
export type PlanGate =
  | { kind: 'feature'; feature: string; requiredTier: BillingTier; message?: string }
  | {
    kind: 'limit';
    limit: string;
    current: number | null;
    max: number | null;
    requiredTier: BillingTier;
    message?: string;
    /** Period quotas (responses, aiGenerations, emailInvites): when the allowance resets. */
    periodEnd?: string | null;
    /** The limit comes from the free trial rather than a paid plan (e.g. extra workspaces). */
    limitedByTrial?: boolean;
  };

export interface VerificationGate {
  feature: string | null;
  message?: string;
}

const GATE_HANDLED = '__responsedockGateHandled';

const TIERS: BillingTier[] = ['STARTER', 'PRO', 'BUSINESS', 'ENTERPRISE'];

interface ErrorBody {
  error?: unknown;
  message?: unknown;
  code?: unknown;
  feature?: unknown;
  limit?: unknown;
  current?: unknown;
  max?: unknown;
  requiredTier?: unknown;
  periodEnd?: unknown;
  limitedByTrial?: unknown;
}

function errorBody(error: unknown): { status?: number; data?: ErrorBody } {
  const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response;
  if (!response) return {};
  const data = response.data && typeof response.data === 'object' ? response.data as ErrorBody : undefined;
  return { status: response.status, data };
}

function asTier(value: unknown): BillingTier {
  const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
  if (normalized === 'PLATFORM') return 'ENTERPRISE';
  return (TIERS as string[]).includes(normalized) ? normalized as BillingTier : 'PRO';
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

export function parsePlanGate(error: unknown): PlanGate | null {
  const { status, data } = errorBody(error);
  if (status !== 403 || !data) return null;
  if (data.code === 'PLAN_FEATURE_REQUIRED') {
    return { kind: 'feature', feature: asText(data.feature) ?? 'this feature', requiredTier: asTier(data.requiredTier), message: asText(data.error) };
  }
  if (data.code === 'PLAN_LIMIT_REACHED') {
    return {
      kind: 'limit',
      limit: asText(data.limit) ?? 'usage',
      current: asNumber(data.current),
      max: asNumber(data.max),
      requiredTier: asTier(data.requiredTier),
      message: asText(data.error),
      periodEnd: asText(data.periodEnd) ?? null,
      limitedByTrial: data.limitedByTrial === true,
    };
  }
  return null;
}

export function parseVerificationGate(error: unknown): VerificationGate | null {
  const { status, data } = errorBody(error);
  if (status !== 403 || data?.code !== 'EMAIL_VERIFICATION_REQUIRED') return null;
  return { feature: asText(data.feature) ?? null, message: asText(data.error) };
}

/** Marks an error whose dialog the global handler already opened, so pages don't show it twice. */
export function markGateHandled(error: unknown) {
  if (error && typeof error === 'object') {
    Object.defineProperty(error, GATE_HANDLED, { value: true, configurable: true });
  }
}

export function isGateHandled(error: unknown): boolean {
  return !!error && typeof error === 'object' && (error as Record<string, unknown>)[GATE_HANDLED] === true;
}

/** True for any plan or verification gate, handled globally or not. */
export function isGateError(error: unknown): boolean {
  return !!parsePlanGate(error) || !!parseVerificationGate(error);
}

/** The message a page shows for a failed request, or null when a global dialog already explains it. */
export function apiErrorMessage(error: unknown, fallback: string): string | null {
  if (isGateHandled(error)) return null;
  const { data } = errorBody(error);
  return asText(data?.error) ?? asText(data?.message) ?? (error instanceof Error && error.message ? error.message : fallback);
}

const FEATURE_LABELS: Record<string, string> = {
  webhooks: 'webhooks',
  webhook: 'webhooks',
  sdk: 'SDK keys',
  sdkKeys: 'SDK keys',
  apiKeys: 'API keys',
  api_keys: 'API keys',
  publicApiKeys: 'SDK keys',
  customDomain: 'a custom domain',
  custom_domain: 'a custom domain',
  hideBranding: 'hiding ResponseDock branding',
  hidePlatformBranding: 'hiding ResponseDock branding',
  whiteLabel: 'white-label branding',
  white_label: 'white-label branding',
  branding: 'your own branding',
  emailDistribution: 'email invitations',
  email_distribution: 'email invitations',
  emailInvites: 'email invitations',
  zapier: 'Zapier',
  chatNotifications: 'Slack and Teams notifications',
  chat_notifications: 'Slack and Teams notifications',
  slack: 'Slack notifications',
  embed: 'embedded workspaces',
  embeddedWorkspace: 'embedded workspaces',
  embedSessions: 'embedded workspaces',
  registrationForms: 'registration forms',
  ai: 'AI survey generations',
  teamInvitations: 'team invitations',
  responseNotifications: 'response notification emails',
  billingManagement: 'billing management',
  aiGenerations: 'AI survey generations',
  identityVerification: 'identity verification',
  triggers: 'audience targeting',
  audienceTargeting: 'audience targeting',
};

const LIMIT_LABELS: Record<string, string> = {
  surveys: 'surveys',
  responses: 'responses this month',
  responsesPerMonth: 'responses this month',
  aiGenerations: 'AI survey generations this month',
  aiGenerationsPerMonth: 'AI survey generations this month',
  emailInvites: 'email invitations this month',
  emailInvitesPerMonth: 'email invitations this month',
  workspaces: 'workspaces',
  collaborators: 'team members',
  teamMembers: 'team members',
  members: 'team members',
  seats: 'team seats',
  webhooks: 'webhooks',
};

function humanize(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
}

export function featureLabel(feature: string | null | undefined): string {
  if (!feature) return 'this feature';
  // Already a readable label ("more AI survey generations"), not an API key.
  if (/\s/.test(feature.trim())) return feature.trim();
  return FEATURE_LABELS[feature] ?? humanize(feature);
}

export function limitLabel(limit: string): string {
  return LIMIT_LABELS[limit] ?? humanize(limit);
}

/** "Confirm your email to unlock webhooks." */
export function verificationFeatureLabel(feature: string | null | undefined): string {
  if (!feature) return 'this feature';
  return featureLabel(feature);
}
